import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Form, Input, message, Spin } from "antd";
import { httpService } from "../../../services/httpService.service";
import "./styles.scss";
import MessagePopUp from "../../components/Messages/MessagePopUp";
import exclamationMark from "../../../Assets/svg/exclamationMark.svg";
import Done from "../../../Assets/svg/done.svg";
import { fileService } from "../../../services/fileService.service";
import { useForm } from "antd/es/form/Form";
import { startSession } from "../../../services/tokenRefresh";

const ConfirmationCode = () => {
  const [form] = useForm();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [confLoader, setConfLoader] = useState(false);
  const [resendTimer, setResendTimer] = useState(false);
  const [countdown, setCountdown] = useState(60);

  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || localStorage.getItem("email");
  const [message, setMessage] = useState({
    trigger: false,
    type: "",
    icon: "",
    title: "",
    text: "",
  });

  // Save email to local storage on initial render or when email is available
  useEffect(() => {
    if (!email) {
      navigate("/login");
    }
  }, [email, navigate]);

  const saveImageAsBase64 = async (imageUrl, localStorageKey) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      const reader = new FileReader();
      reader.readAsDataURL(blob);
      return new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64data = reader.result;
          localStorage.setItem(localStorageKey, base64data);
          resolve(base64data);
        };
        reader.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error("Error saving image:", error);
      throw error;
    }
  };

  const getUserProfilePicture = async (fileKey) => {
    const url = `/FileData/getFileByFileKey?fileKey=${fileKey}`;
    await fileService.get(
      url,
      (response) => {
        const profilePictureUrl = response?.data?.data;
        saveImageAsBase64(profilePictureUrl, "profilePicture");
      },
      (error) => {
        // console.log(error);
      }
    );
  };

  const handleVerify = async () => {
    setLoading(true);

    form
      .validateFields()
      .then((values) => {
        httpService.post(
          "/api/Account/confirmCode",
          {
            email,
            ...(code ? { code } : {}),
          },
          (response) => {
            const message = response.message;
            const {
              roles,
              lastLoggedInDate,
              fullName,
              id,
              profilePictureUrl,
            } = response.data;
            startSession(response.data);
            localStorage.setItem("userEmail", email);
            localStorage.setItem("username", fullName);
            localStorage.setItem("role", roles[0]?.roleName);
            localStorage.setItem("roleId", roles[0]?.id);
            localStorage.setItem("userID", id);
            getUserProfilePicture(response?.data?.profilePictureUrl);

            setLoading(false);
            setMessage({
              trigger: true,
              type: "success",
              icon: Done,
              title: "Confirmation!",
              text: message || "Code verified!",
            });
            setTimeout(() => {
              setMessage({
                trigger: false,
              });
            }, 3000);

            if (lastLoggedInDate === null) {
              navigate("/first-time");
            } else {
              navigate("/dashboard/contracts");
            }
          },
          (error) => {
            const errorMessage =
              error?.errors?.[0]?.message || error?.message || "Invalid code!";

            setMessage({
              trigger: true,
              type: "danger",
              icon: exclamationMark,
              title: "Oops! A problem has occurred!",
              text: errorMessage,
            });
            setTimeout(() => {
              setMessage({
                trigger: false,
              });
            }, 3000);
            setLoading(false);
            localStorage.removeItem("token");
            localStorage.removeItem("userEmail");
            localStorage.removeItem("username");
            localStorage.removeItem("role");
          }
        );
      })
      .catch(() => {
        setLoading(false);
        setMessage({
          trigger: true,
          type: "danger",
          icon: exclamationMark,
          title: "Oops! A problem has occurred!",
          text: "Please enter a valid code.",
        });
        setTimeout(() => setMessage({ trigger: false }), 3000);
      });
  };

  const handleResendCode = (e) => {
    e.preventDefault();
    setConfLoader(true);
    setResendTimer(true);
    setCountdown(60); // Start from 60 seconds

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          setResendTimer(false);
          return 60;
        }
        return prev - 1;
      });
    }, 1000);

    httpService.get(
      `/api/Account/resendCode?email=${email}`,
      (res) => {
        setConfLoader(false);
        setMessage({
          trigger: true,
          type: "success",
          icon: Done,
          title: "Confirmation!",
          text: res?.message || "Verification code resent to your email.",
        });
        setTimeout(() => setMessage({ trigger: false }), 3000);
      },
      (error) => {
        setConfLoader(false);
        setMessage({
          trigger: true,
          type: "danger",
          icon: exclamationMark,
          title: "Oops! A problem has occurred!",
          text:
            error?.errors?.[0]?.message ||
            error?.message ||
            "Failed to resend code.",
        });
        setTimeout(() => setMessage({ trigger: false }), 3000);
      }
    );
  };

  const handleCodeChange = (value) => {
    const isAnumber = /^\d+$/.test(value);

    if (isAnumber) {
      if (value?.length < 7 || value?.length === 0) {
        setCode(value);
        // console.log("Confirm code:", code);
      } else {
        setMessage({
          trigger: true,
          type: "danger",
          icon: exclamationMark,
          title: "Oops! A problem has occurred!",
          text: "You can not add more than 6 characters!",
        });
        setTimeout(() => {
          setMessage({
            trigger: false,
          });
        }, 3000);
      }
    } else {
      setMessage({
        trigger: true,
        type: "danger",
        icon: exclamationMark,
        title: "Oops! A problem has occurred!",
        text: "The confirmation code must have only numbers",
      });
      setTimeout(() => {
        setMessage({
          trigger: false,
        });
      }, 3000);
    }
  };

  return (
    <div className="wrapper">
      <div className="row">
        <div className="col-md-6 left-column">
          <img
            src="/assets/svg/LoginImage.svg"
            alt="Login Visual"
            className="leftImage"
          />
        </div>
        <MessagePopUp
          trigger={message.trigger}
          type={message.type}
          icon={message.icon}
          messageTitle={message.title}
          messageText={message.text}
        />
        <div className="col-md-6 right-column">
          <div className="form-wrapper1">
            <h2 className="verificationCodeHeader">Verification Code</h2>
            <p className="verificationCodeText">
              Please enter the verification code that we sent to {email}
            </p>
            <Form
              form={form}
              layout="vertical"
              className="w-100 d-flex flex-column align-items-center justify-content-center"
            >
              <div className="OTP w-100 d-flex justify-content-center">
                <Form.Item
                  name="code"
                  className=" text-center"
                  rules={[
                    {
                      required: true,
                      message: "Code is required",
                    },
                    {
                      pattern: /^\d{6}$/,
                      message: "Code must be exactly 6 digits",
                    },
                  ]}
                >
                  <Input.OTP
                    type="number"
                    maxLength={6}
                    value={code}
                    onChange={(val) => {
                      const onlyDigits = val.replace(/\D/g, "").slice(0, 6);
                      handleCodeChange(onlyDigits);
                    }}
                    placeholder="Enter code"
                  />
                </Form.Item>
              </div>

              <div className="d-flex flex-column buttonsModal w-100 mt-4">
                <Button
                  className="addButtonTPM w-50 mx-auto"
                  key="submit"
                  type="primary"
                  onClick={handleVerify}
                  loading={loading || confLoader}
                  disabled={loading}
                >
                  {loading
                    ? "Verifying code..."
                    : confLoader
                    ? "Sending code..."
                    : "Verify code"}
                </Button>
                {resendTimer ? (
                  <span className="w-100 text-center" style={{ color: "#999" }}>
                    Please wait {countdown}s
                  </span>
                ) : (
                  <p className="verificationCodeText">
                    If you didn’t receive a verification code,{" "}
                    <span
                      style={{ color: "#E164A0", cursor: "pointer" }}
                      onClick={handleResendCode}
                    >
                      click here to resend it
                    </span>{" "}
                    or send us an email at info@kaptechgroup.com
                  </p>
                )}
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationCode;
